import React, { FC } from 'react';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { Button, Icon, TextLink } from '@kontentino/ui';
import { faArrowUpRight } from '@fortawesome/pro-regular-svg-icons/faArrowUpRight';
import { TYPE } from '@kontentino/kontentino-constants/Pages';
import { SocialLoginEmptyReason } from 'types/SocialLogin';
import { guideLinks } from 'components/modals/newSocialMediaProfile/errorStepsGuide/errorStepsGuide';

// Meta's own help article on creating a business portfolio.
const CREATE_PORTFOLIO_URL =
  'https://www.facebook.com/business/help/1710077379203657';

// Every reason with a screen. A Record, so a reason added to the type is a
// type error here until it is listed.
const KNOWN_REASONS: Record<SocialLoginEmptyReason, true> = {
  no_portfolios: true,
  no_pages_for_network: true,
  portfolios_unreadable: true,
};

const isKnownReason = (reason: string): reason is SocialLoginEmptyReason =>
  Object.keys(KNOWN_REASONS).includes(reason);

/**
 * The one place a poll's `empty_reason` becomes a screen. An older
 * social-login sends none and a newer one may send a reason this build does
 * not know: both read as having no business portfolio, so the copy, the
 * button and the test id always agree.
 */
const toOboEmptyReason = (reason?: string | null): SocialLoginEmptyReason =>
  reason && isKnownReason(reason) ? reason : 'no_portfolios';

type Props = {
  // As the poll sent it.
  reason?: SocialLoginEmptyReason | null;
  pageType: number;
  // Restarts Safe Connect from the beginning.
  onTryAgain: () => void;
};

// Full literal keys, so a search for a key finds its use.
const copyOf = (
  t: TFunction,
  reason: SocialLoginEmptyReason,
  pageType: number,
): { title: string; body: string | null } => {
  switch (reason) {
    case 'no_pages_for_network':
      return {
        title:
          pageType === TYPE.INSTAGRAM
            ? t('newSocialMediaProfile.oboEmpty.noPagesTitle_instagram')
            : t('newSocialMediaProfile.oboEmpty.noPagesTitle_facebook'),
        body: t('newSocialMediaProfile.oboEmpty.noPagesBody'),
      };
    case 'portfolios_unreadable':
      return {
        title: t('newSocialMediaProfile.oboEmpty.unreadableTitle'),
        body: null,
      };
    default:
      return {
        title: t('newSocialMediaProfile.oboEmpty.noPortfoliosTitle'),
        body: t('newSocialMediaProfile.oboEmpty.noPortfoliosBody'),
      };
  }
};

// What the Safe Connect picker shows in place of its two columns when the poll
// found nothing to list. Which screen depends on why (`empty_reason`).
const OboEmptyState: FC<Props> = ({
  reason: sentReason,
  pageType,
  onTryAgain,
}) => {
  const { t } = useTranslation();
  const reason = toOboEmptyReason(sentReason);
  const guideLink = guideLinks[pageType as keyof typeof guideLinks] as
    | string
    | undefined;
  const { title, body } = copyOf(t, reason, pageType);

  return (
    <div
      data-cy={`obo-empty_${reason}`}
      className="tw-flex tw-flex-col tw-items-center tw-gap-6 tw-py-8 tw-text-center"
    >
      <div className="tw-flex tw-max-w-[360px] tw-flex-col tw-gap-3">
        <h2 className="tw-m-0 tw-text-md tw-font-semibold tw-text-grayscale-180">
          {title}
        </h2>
        {body && (
          <p className="tw-m-0 tw-text-md tw-text-grayscale-140">{body}</p>
        )}
      </div>

      {reason === 'no_portfolios' && (
        <>
          {/* A link, not a button: it opens Meta's article. The classes are
              the small primary Button's (the component only renders a
              <button>, and a link inside one is not valid HTML). */}
          <a
            href={CREATE_PORTFOLIO_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-cy="obo-empty_create-portfolio"
            className="tw-inline-flex tw-h-7 tw-items-center tw-justify-center tw-gap-1 tw-rounded tw-bg-primary-100 tw-px-3 tw-text-sm tw-font-medium tw-text-white tw-transition-colors hover:tw-bg-primary-120"
          >
            {t('newSocialMediaProfile.oboEmpty.createPortfolio')}
            <span className="tw-inline-flex">
              <Icon icon={faArrowUpRight} />
            </span>
          </a>
          <p className="tw-m-0 tw-text-sm tw-text-grayscale-100">
            {t('newSocialMediaProfile.oboEmpty.returnHint')}
          </p>
        </>
      )}

      {reason !== 'no_portfolios' && (
        <div className="tw-flex tw-flex-col tw-items-center tw-gap-4">
          {reason === 'no_pages_for_network' && guideLink && (
            <TextLink>
              <a
                href={guideLink}
                target="_blank"
                rel="noopener noreferrer"
                className="tw-text-sm tw-font-semibold tw-underline"
                data-cy="obo-empty_setup-guide"
              >
                {t('newSocialMediaProfile.twoColumn.setupGuide.link')}
              </a>
            </TextLink>
          )}
          <Button
            size="small"
            onClick={onTryAgain}
            data-cy="obo-empty_try-again"
          >
            {t('newSocialMediaProfile.oboEmpty.tryAgain')}
          </Button>
        </div>
      )}
    </div>
  );
};

export default OboEmptyState;
